import React, { useState, useEffect, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '../ui';

// Self-contained DEMO ticket wallet. No backend, no Stripe, no real payment.
// Demonstrates the recommended wallet model:
//   - one purchase issues N INDIVIDUAL tickets, each with its own unique QR
//   - each ticket can be transferred (by email/phone) - simulated
//   - tap-to-enter checks in a SINGLE ticket; a second scan is rejected
//   - tickets persist to localStorage (the "wallet" / lost-phone story)

const parsePrice = (p) => parseFloat(String(p || '').replace(/[^0-9.]/g, '')) || 0;
const rand = () => Math.random().toString(36).slice(2, 6).toUpperCase();
const makeCode = () => `VT-${rand()}${Date.now().toString(36).slice(-3).toUpperCase()}`;
const fmtMoney = (n) => `$${n.toFixed(2)}`;
const nowTime = () => new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

const STAGES = [
  { p: 0, icon: '🎟️', label: 'Ticket Verified' },
  { p: 34, icon: '🚶', label: 'At the Gate' },
  { p: 67, icon: '📷', label: 'Scanning' },
  { p: 100, icon: '🎉', label: "You're In!" },
];

const DemoCheckout = ({ open, onClose, event, district }) => {
  const [step, setStep] = useState('checkout'); // checkout | wallet
  const [qty, setQty] = useState(2);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [tickets, setTickets] = useState([]);
  const [transferId, setTransferId] = useState(null);
  const [transferTo, setTransferTo] = useState('');
  const [entering, setEntering] = useState(null); // ticket id being scanned
  const [progress, setProgress] = useState(0);
  const timer = useRef(null);

  const primary = district?.colors?.primary || '#1e3a8a';
  const accent = district?.colors?.accent || '#fbbf24';
  const storeKey = event ? `vt-demo-wallet-${district?.slug}-${event.title}` : null;

  // On open: restore any saved wallet for this event (lost-phone / persistence demo).
  useEffect(() => {
    if (!open || !event) return;
    setTransferId(null); setEntering(null); setProgress(0);
    try {
      const saved = storeKey && JSON.parse(localStorage.getItem(storeKey) || 'null');
      if (saved && saved.tickets && saved.tickets.length) {
        setTickets(saved.tickets); setName(saved.name || ''); setEmail(saved.email || ''); setQty(saved.tickets.length);
        setStep('wallet'); return;
      }
    } catch { /* ignore */ }
    setStep('checkout'); setQty(2); setName(''); setEmail(''); setTickets([]);
  }, [open, event, storeKey]);

  const persist = (list, nm, em) => {
    try { storeKey && localStorage.setItem(storeKey, JSON.stringify({ tickets: list, name: nm, email: em })); } catch { /* ignore */ }
  };

  // Drive the tap-to-enter tracker for the active ticket.
  useEffect(() => {
    if (!entering) return;
    setProgress(0);
    timer.current = setInterval(() => {
      setProgress((p) => {
        if (p >= 100) {
          clearInterval(timer.current);
          // mark THAT ticket checked in (atomic, per-ticket dedupe)
          setTickets((list) => {
            const next = list.map((t) => t.id === entering ? { ...t, status: 'checked_in', enteredAt: nowTime() } : t);
            persist(next, name, email);
            return next;
          });
          setTimeout(() => setEntering(null), 900);
          return 100;
        }
        return Math.min(100, p + 2);
      });
    }, 40);
    return () => clearInterval(timer.current);
  }, [entering]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open || !event) return null;

  const unit = parsePrice(event.price);
  const total = unit * qty;
  const subtitle = event.home ? `${event.home}${event.away ? ` vs ${event.away}` : ''}` : event.school;

  const completePurchase = () => {
    const list = Array.from({ length: qty }, (_, i) => ({
      id: `${Date.now()}-${i}-${rand()}`,
      code: makeCode(),
      n: i + 1,
      status: 'valid',
      enteredAt: null,
      transferredTo: null,
      holder: name || 'You',
    }));
    setTickets(list); persist(list, name, email); setStep('wallet');
  };

  const doTransfer = (id) => {
    const to = transferTo.trim();
    if (!to) return;
    setTickets((list) => {
      const next = list.map((t) => t.id === id ? { ...t, transferredTo: to } : t);
      persist(next, name, email);
      return next;
    });
    setTransferId(null); setTransferTo('');
  };

  const resetWallet = () => {
    try { storeKey && localStorage.removeItem(storeKey); } catch { /* ignore */ }
    setTickets([]); setStep('checkout'); setQty(2); setName(''); setEmail('');
  };

  const activeTicket = tickets.find((t) => t.id === entering);
  const currentStage = STAGES.filter((s) => progress >= s.p).pop() || STAGES[0];
  const entered = progress >= 100;
  const checkedInCount = tickets.filter((t) => t.status === 'checked_in').length;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-ink-900/60 backdrop-blur-sm" onClick={onClose} role="dialog" aria-modal="true">
      <div className="bg-white rounded-3xl shadow-lift w-full max-w-md overflow-hidden animate-fade-in-up max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-4 text-white flex items-center justify-between flex-shrink-0" style={{ backgroundColor: primary }}>
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-widest opacity-80">
              {step === 'checkout' ? 'Checkout' : 'Your ticket wallet'}
            </p>
            <h3 className="text-lg font-bold font-display truncate">{event.title}</h3>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-white/80 hover:text-white text-xl leading-none px-1">×</button>
        </div>

        {/* ---------- CHECKOUT ---------- */}
        {step === 'checkout' && (
          <div className="p-6 overflow-y-auto">
            {subtitle && <p className="font-medium text-ink-700 text-sm">{subtitle}</p>}
            {event.venue && <p className="text-sm text-ink-500">📍 {event.venue}</p>}

            <div className="flex items-center justify-between py-3 border-y border-ink-100 mt-4">
              <div>
                <p className="font-semibold text-ink-900">General Admission</p>
                <p className="text-sm text-ink-500">{fmtMoney(unit)} each · one ticket per guest</p>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-9 h-9 rounded-full bg-ink-100 text-ink-700 font-bold text-lg hover:bg-ink-200" aria-label="Decrease">−</button>
                <span className="w-6 text-center font-bold text-ink-900">{qty}</span>
                <button onClick={() => setQty((q) => Math.min(10, q + 1))} className="w-9 h-9 rounded-full text-white font-bold text-lg hover:opacity-90" style={{ backgroundColor: primary }} aria-label="Increase">+</button>
              </div>
            </div>

            <div className="mt-4 space-y-3">
              <input type="text" placeholder="Full name" value={name} onChange={(e) => setName(e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-ink-200 outline-none focus:ring-2 focus:ring-brand-300" />
              <input type="email" placeholder="Email for your tickets" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-ink-200 outline-none focus:ring-2 focus:ring-brand-300" />
            </div>

            <div className="mt-5 flex items-center justify-between">
              <span className="text-ink-500">Total · {qty} separate ticket{qty > 1 ? 's' : ''}</span>
              <span className="text-2xl font-bold font-display text-ink-900">{fmtMoney(total)}</span>
            </div>

            <Button onClick={completePurchase} className="mt-4 w-full text-white" style={{ backgroundColor: primary }} size="lg">Pay {fmtMoney(total)}</Button>
            <p className="mt-2 text-center text-xs text-ink-400">Demo checkout · no real payment is processed</p>
          </div>
        )}

        {/* ---------- WALLET ---------- */}
        {step === 'wallet' && (
          <div className="p-5 overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-ink-500">
                {tickets.length} ticket{tickets.length > 1 ? 's' : ''} · {checkedInCount} checked in
              </p>
              <button onClick={resetWallet} className="text-xs text-ink-400 hover:text-ink-600 underline">Start over</button>
            </div>
            <p className="text-xs text-ink-400 mb-4">🔒 Saved to your wallet — each guest gets their own ticket. Sign in on any device to access (demo).</p>

            <div className="space-y-3">
              {tickets.map((t) => (
                <div key={t.id} className="rounded-2xl border border-ink-100 overflow-hidden">
                  <div className="h-1" style={{ backgroundColor: t.status === 'checked_in' ? '#10b981' : accent }} />
                  <div className="p-4 flex gap-4">
                    <div className="flex-shrink-0 text-center">
                      <div className={`rounded-xl border border-ink-100 p-1.5 ${t.status === 'checked_in' ? 'opacity-40' : ''}`}>
                        <QRCodeSVG value={`VT-DEMO|${district?.slug}|${event.title}|${t.code}`} size={72} fgColor={primary} level="M" />
                      </div>
                      <p className="text-[10px] text-ink-400 mt-1">{t.code}</p>
                    </div>
                    <div className="min-w-0 flex-grow">
                      <div className="flex items-center justify-between">
                        <p className="font-semibold text-ink-900 text-sm">Ticket {t.n} of {tickets.length}</p>
                        {t.status === 'checked_in' ? (
                          <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">Admitted {t.enteredAt}</span>
                        ) : (
                          <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-ink-100 text-ink-600">Valid</span>
                        )}
                      </div>
                      <p className="text-xs text-ink-500 mt-0.5 truncate">
                        {t.transferredTo ? <>👤 Transferred to <span className="font-medium text-ink-700">{t.transferredTo}</span></> : `Holder: ${t.holder}`}
                      </p>

                      {transferId === t.id ? (
                        <div className="mt-2 flex gap-2">
                          <input value={transferTo} onChange={(e) => setTransferTo(e.target.value)} placeholder="Email or phone" className="flex-grow px-3 py-1.5 text-sm rounded-lg border border-ink-200 outline-none focus:ring-2 focus:ring-brand-300" />
                          <button onClick={() => doTransfer(t.id)} className="px-3 py-1.5 text-sm rounded-lg text-white font-medium" style={{ backgroundColor: primary }}>Send</button>
                        </div>
                      ) : (
                        <div className="mt-2 flex gap-2">
                          {t.status === 'checked_in' ? (
                            <button disabled className="px-3 py-1.5 text-sm rounded-lg bg-ink-100 text-ink-400 cursor-not-allowed">Already used</button>
                          ) : (
                            <button onClick={() => setEntering(t.id)} className="px-3 py-1.5 text-sm rounded-lg text-white font-medium" style={{ backgroundColor: accent, color: primary }}>👆 Tap to enter</button>
                          )}
                          {t.status !== 'checked_in' && (
                            <button onClick={() => { setTransferId(t.id); setTransferTo(''); }} className="px-3 py-1.5 text-sm rounded-lg border border-ink-200 text-ink-600 hover:bg-ink-50">Transfer</button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <p className="text-center text-xs text-ink-400 mt-4">
              Each ticket scans in once. A second scan of the same QR is rejected — screenshots can't get two people in.
            </p>
          </div>
        )}
      </div>

      {/* ---------- ENTRY TRACKER OVERLAY (per ticket) ---------- */}
      {entering && activeTicket && (
        <div className="absolute inset-0 z-[110] flex items-center justify-center p-4 bg-ink-900/70" onClick={(e) => e.stopPropagation()}>
          <div className="bg-white rounded-3xl shadow-lift w-full max-w-sm p-6 text-center animate-fade-in-up">
            <p className="text-xs uppercase tracking-widest text-ink-400">Ticket {activeTicket.n} · {activeTicket.code}</p>
            <div className={`mx-auto my-3 w-14 h-14 rounded-full flex items-center justify-center text-2xl ${entered ? 'bg-emerald-500 text-white' : ''}`} style={!entered ? { backgroundColor: `${primary}14`, color: primary } : {}}>
              {entered ? '✓' : currentStage.icon}
            </div>
            <h3 className="text-lg font-bold font-display text-ink-900">{entered ? "You're in! 🎉" : currentStage.label}</h3>
            <div className="relative px-1 mt-5 mb-1">
              <div className="absolute left-3 right-3 top-4 h-1.5 bg-ink-100 rounded-full" />
              <div className="absolute left-3 top-4 h-1.5 rounded-full transition-all duration-100 ease-linear" style={{ width: `calc((100% - 24px) * ${progress / 100})`, backgroundColor: entered ? '#10b981' : primary }} />
              <div className="relative flex justify-between">
                {STAGES.map((s, i) => {
                  const on = progress >= s.p;
                  return (
                    <div key={s.label} className="flex flex-col items-center" style={{ width: 64 }}>
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm transition-all" style={{ backgroundColor: on ? (entered ? '#10b981' : primary) : '#f1f5f9', color: on ? '#fff' : '#94a3b8' }}>
                        {progress > s.p && i < STAGES.length - 1 ? '✓' : s.icon}
                      </div>
                      <span className={`mt-1 text-[9px] leading-tight text-center ${on ? 'text-ink-700 font-semibold' : 'text-ink-400'}`}>{s.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            {entered && <p className="text-sm text-ink-500 mt-3">This ticket is now used and can't be scanned again.</p>}
          </div>
        </div>
      )}
    </div>
  );
};

export default DemoCheckout;
